import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import CustomModal from '../../../../Components/Modal/CustomModal';
import DropdownNoImage from '../../../../Components/DropDown/DropdownNoImage';
import DropdownNoLabel from '../../../../Components/DropDown/DropdownNoLabel';
import { updateTherapistSchedule, getTimingCodes, getLeaveStatuses } from '../../../../APICalling/APICalling';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import { buildTimeDropdownOptions, doesTimeRangeCrossMidnight, getExtendedDropdownSelectedValue } from '../../../../Utils/utils';
import styles from './WorkHoursPopup.module.css';

// Module-level cache for timing codes and leave statuses (persists across component re-renders)
let cachedTimingCodesByOutlet = {};
let cachedLeaveStatuses = null;
let leaveStatusesFetchPromise = null;

const WorkHoursPopup = ({ isOpen, onClose, selectedDate, weekDates = [], shiftInfo, therapistId, onSuccess, startWithNotWorking = false, leaveAnyway = false, from="homde" }) => {
    const {
        selectedOutlet,
        loggedOutletID,
        homeStaffList,
        setHomeStaffList,
        selectedDate: homeSelectedDate,
    } = useGlobalContext();
    const [mode, setMode] = useState(startWithNotWorking ? 'single' : 'selection'); // 'selection', 'repeating', 'single'
    const [editType, setEditType] = useState('single'); // 'repeating' or 'single'
    const [isNotWorking, setIsNotWorking] = useState(startWithNotWorking);
    const [loading, setLoading] = useState(false);
    
    // Form state for repeating shifts
    const [selectedDay, setSelectedDay] = useState('Monday');
    const [selectedDayRepeating, setSelectedDayRepeating] = useState('Every Monday');
    const [dayToDateMap, setDayToDateMap] = useState({}); // Map day names to actual dates
    const [timingCode, setTimingCode] = useState('');
    const [checkIn, setCheckIn] = useState('10:00');
    const [checkOut, setCheckOut] = useState('20:00');
    const [checkOutExpansion, setCheckOutExpansion] = useState(null); // null = auto-detect, 0 = same day, 1 = +1
    
    // Form state for not working
    const [notWorkingReason, setNotWorkingReason] = useState('');
    const [remarks, setRemarks] = useState('');

    // Error states
    const [errors, setErrors] = useState({
        timingCode: '',
        checkIn: '',
        checkOut: '',
        notWorkingReason: ''
    });

    // Dynamic data
    const outletId = selectedOutlet || loggedOutletID;
    const [timingCodes, setTimingCodes] = useState(() => {
        return outletId && cachedTimingCodesByOutlet[outletId]
            ? cachedTimingCodesByOutlet[outletId]
            : [];
    });
    const [leaveStatuses, setLeaveStatuses] = useState(() =>
        cachedLeaveStatuses?.length ? cachedLeaveStatuses : []
    );

    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const daysRepeating = ['Every Monday', 'Every Tuesday', 'Every Wednesday', 'Every Thursday', 'Every Friday', 'Every Saturday', 'Every Sunday'];

    const formatTimeToHHMM = (timeString) => {
        if (!timeString) return null;
        const timeParts = timeString.split(':');
        if (timeParts.length >= 2) {
            return `${timeParts[0]}:${timeParts[1]}`;
        }
        return timeString;
    };

    const startTimeOptions = useMemo(() => buildTimeDropdownOptions({
        includeNextDay: false,
    }), []);
    const endTimeOptions = useMemo(() => buildTimeDropdownOptions({
        isOutletExtended: true,
        includeNextDay: true,
    }), []);
    const resolvedCheckOutExpansion = useMemo(() => {
        if (checkOutExpansion !== null) return checkOutExpansion;
        if (checkIn && checkOut && doesTimeRangeCrossMidnight(checkIn, checkOut)) return 1;
        return 0;
    }, [checkOutExpansion, checkIn, checkOut]);

    const selectedEndTimeValue = useMemo(() => {
        const normalized = formatTimeToHHMM(checkOut);
        if (!normalized) return '';

        if (resolvedCheckOutExpansion === 1) {
            return `${normalized}+1`;
        }

        return getExtendedDropdownSelectedValue({
            value: checkOut,
            anchorTime: checkIn,
            isOutletExtended: true,
            crossesMidnight: false,
        });
    }, [checkOut, checkIn, resolvedCheckOutExpansion]);
    const shouldShowExpandedEndLabel = resolvedCheckOutExpansion === 1;

    const timeToMinutes = (timeStr) => {
        const normalized = formatTimeToHHMM(timeStr);
        if (!normalized) return null;
        const [hours, minutes] = normalized.split(':').map(Number);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
        return hours * 60 + minutes;
    };

    const isEndAfterStart = (startTime, endTime, endIsNextDay) => {
        const startMinutes = timeToMinutes(startTime);
        let endMinutes = timeToMinutes(endTime);
        if (startMinutes === null || endMinutes === null) return true;
        if (endIsNextDay) endMinutes += 1440;
        return endMinutes > startMinutes;
    };

    const getShiftExpansionFlag = (shift) =>
        Number(shift?.is_schedule_calendar_expansion ?? shift?.is_calendar_expansion) === 1 ? 1 : 0;

    const pendingHourDigitRef = useRef({ checkIn: '', checkOut: '' });

    const applyTimeValue = (field, value) => {
        if (field === 'checkIn') {
            setCheckIn(value);
        } else {
            setCheckOut(value);
        }
        setCheckOutExpansion(null);
        setErrors(prev => ({ ...prev, [field]: '', checkOut: '' }));
    };

    const clearPendingHourDigit = (field) => {
        if (field) {
            pendingHourDigitRef.current[field] = '';
            return;
        }
        pendingHourDigitRef.current.checkIn = '';
        pendingHourDigitRef.current.checkOut = '';
    };

    // Keep hour field open for a 2nd digit when typing 10–23 (e.g. "22" → 10 PM, not 02:02)
    const handleTimeInputKeyDown = (e, field) => {
        if (e.key.length === 1 && /\d/.test(e.key)) {
            const pending = pendingHourDigitRef.current[field];

            if (!pending && (e.key === '1' || e.key === '2')) {
                e.preventDefault();
                pendingHourDigitRef.current[field] = e.key;
                applyTimeValue(field, `0${e.key}:00`);
                return;
            }

            if (pending === '1' || pending === '2') {
                e.preventDefault();
                const combinedHour = parseInt(`${pending}${e.key}`, 10);
                clearPendingHourDigit(field);

                if (combinedHour === 24) {
                    applyTimeValue(field, '00:00');
                } else if (combinedHour >= 10 && combinedHour <= 23) {
                    applyTimeValue(field, `${String(combinedHour).padStart(2, '0')}:00`);
                } else {
                    applyTimeValue(field, `${pending.padStart(2, '0')}:${e.key.padStart(2, '0')}`);
                }
                return;
            }
            return;
        }

        if (['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Backspace', 'Escape'].includes(e.key)) {
            clearPendingHourDigit(field);
        }
    };

    const handleCheckInInputChange = (newValue) => {
        clearPendingHourDigit('checkIn');
        setCheckIn(newValue);
        setCheckOutExpansion(null);
        setErrors(prev => ({ ...prev, checkIn: '', checkOut: '' }));
    };

    // Handle end time typing — no start/end validation until Update Schedule
    const handleCheckOutInputChange = (newValue) => {
        clearPendingHourDigit('checkOut');
        setCheckOut(newValue);
        setCheckOutExpansion(null);
        setErrors(prev => ({ ...prev, checkOut: '' }));
    };

    const handleTimeDropdownSelect = (field, option) => {
        clearPendingHourDigit();
        // Start time should always stay on the same-day timeline.
        const rawTimeValue = option?.timeValue || option?.value || option;
        const rawOptionValue = option?.value ?? '';
        const time24 = typeof rawTimeValue === 'string'
            ? rawTimeValue.replace(/\+1$/, '')
            : rawTimeValue;
        if (field === 'checkIn') {
            setCheckIn(time24);
            setCheckOutExpansion(null);
            setErrors(prev => ({ ...prev, checkIn: '', checkOut: '' }));
        } else if (field === 'checkOut') {
            const isNextDay = option?.isNextDay === true
                || (typeof rawOptionValue === 'string' && rawOptionValue.endsWith('+1'));
            setCheckOut(time24);
            setCheckOutExpansion(isNextDay ? 1 : 0);
            setErrors(prev => ({ ...prev, checkOut: '' }));
        }
    };

    // Handle time input click to open dropdown
    const handleTimeInputClick = (e, field, currentValue) => {
        try {
            const inputElement = e.target;
            // Traverse DOM manually to avoid CSS module selector issues with closest()
            // CSS module class names can contain special characters that break CSS selectors
            let currentElement = inputElement;
            let timeInputGroup = null;
            let dropdownWrapper = null;
            
            // Get the actual class names from styles object
            const timeInputGroupClass = styles.timeInputGroup;
            const timeDropdownWrapperClass = styles.timeDropdownWrapper;
            
            // Find the timeInputGroup by traversing up the DOM
            // Start from the input element itself and traverse up to find timeInputGroup
            while (currentElement && currentElement !== document.body) {
                if (currentElement.classList && currentElement.classList.contains(timeInputGroupClass)) {
                    timeInputGroup = currentElement;
                    break;
                }
                currentElement = currentElement.parentElement;
            }
            
            if (timeInputGroup) {
                // Find dropdownWrapper within timeInputGroup (it's a sibling of timeInputWrapper)
                const children = Array.from(timeInputGroup.children);
                dropdownWrapper = children.find(child => 
                    child.classList && child.classList.contains(timeDropdownWrapperClass)
                );
                
                if (dropdownWrapper) {
                    const dropdownToggle = dropdownWrapper.querySelector('.dropdownToggle');
                    if (dropdownToggle) {
                        // Enable pointer events on wrapper before opening
                        dropdownWrapper.style.pointerEvents = 'auto';
                        
                        // Programmatically click the dropdown toggle
                        dropdownToggle.click();
                        
                        // Use setTimeout to ensure dropdown menu is visible
                        setTimeout(() => {
                            const dropdownMenu = dropdownWrapper.querySelector('.dropdownMenu');
                            if (dropdownMenu) {
                                // Ensure menu and all its children have pointer events
                                dropdownMenu.style.pointerEvents = 'auto';
                                dropdownMenu.style.zIndex = '10001';
                                const allItems = dropdownMenu.querySelectorAll('*');
                                allItems.forEach(item => {
                                    item.style.pointerEvents = 'auto';
                                });
                            }
                        }, 50);
                    }
                }
            }
        } catch (error) {
            // Silently fail - don't break the UI if dropdown opening fails
            console.error('Error opening time dropdown:', error);
        }
    };

    // Handle time input double click - focus input for typing
    const handleTimeInputDoubleClick = (e, field) => {
        clearPendingHourDigit(field);
        const inputElement = e.target;
        inputElement.focus();
        inputElement.select();
    };

    // Fetch timing codes and leave statuses from API (cache only non-empty leave lists)
    useEffect(() => {
        if (outletId) {
            const cachedCodes = cachedTimingCodesByOutlet[outletId];
            if (cachedCodes) {
                setTimingCodes(cachedCodes);
            } else {
                fetchTimingCodes(outletId);
            }
        }
        if (cachedLeaveStatuses?.length) {
            setLeaveStatuses(cachedLeaveStatuses);
        } else {
            fetchLeaveStatuses();
        }
    }, [outletId]);

    // If Not Working is on but reasons are empty (race / failed fetch / empty cache), retry
    useEffect(() => {
        if (isOpen && isNotWorking && leaveStatuses.length === 0) {
            fetchLeaveStatuses();
        }
    }, [isOpen, isNotWorking, leaveStatuses.length]);

    const fetchTimingCodes = async (outletIdParam) => {
        try {
            const response = await getTimingCodes({ 
                pagination: 0,
                outlet: outletIdParam
            });
            
            if (response?.data?.success) {
                // Access the timing_code array from the nested structure
                const codes = response.data.data?.list?.timing_code || [];
                // Map timing codes to dropdown format with id, code, and times
                const mappedCodes = codes.map(code => {
                    // Format times from "10:00:00" to "10:00"
                    const startTime = code.start_time ? code.start_time.substring(0, 5) : '';
                    const endTime = code.end_time ? code.end_time.substring(0, 5) : '';
                    // Create label with code and times: "CQT03 (10:00 - 20:30)"
                    const label = startTime && endTime ? `${code.code} (${startTime} - ${endTime})` : code.code;
                    
                    return {
                        id: code.id,
                        code: code.code,
                        label: label,
                        value: code.id?.toString(),
                        start_time: code.start_time,
                        end_time: code.end_time
                    };
                });
                
                // Add "Custom Schedule" as the first option
                const customOption = {
                    id: 'custom',
                    code: 'Custom Schedule',
                    label: 'Custom Schedule',
                    value: 'custom',
                    start_time: '00:00',
                    end_time: '00:00'
                };
                
                const codesWithCustom = [customOption, ...mappedCodes];
                
                // Cache at module level
                cachedTimingCodesByOutlet[outletIdParam] = codesWithCustom;
                setTimingCodes(codesWithCustom);
            }
        } catch (error) {
            console.error('Error fetching timing codes:', error);
            // Don't show error toast as it's not critical - will use fallback if needed
        }
    };

    const fetchLeaveStatuses = async () => {
        // Reuse in-flight request so rapid open / Not Working toggles don't race
        if (leaveStatusesFetchPromise) {
            try {
                const statuses = await leaveStatusesFetchPromise;
                if (statuses?.length) {
                    setLeaveStatuses(statuses);
                }
            } catch (error) {
                // Error already logged by the original request
            }
            return;
        }

        leaveStatusesFetchPromise = (async () => {
            const response = await getLeaveStatuses({
                pagination: 0,
                type: 'leave'
            });

            if (!response?.data?.success) {
                return [];
            }
            // Prefer nested list shape; fall back to other common API shapes
            const statuses = response.data.data?.list?.statuses || response.data.data?.statuses || response.data.list?.statuses || [];

            return statuses.map(status => ({
                label: status.name,
                value: status.name,
                id: status.id
            }));
        })();

        try {
            const mappedStatuses = await leaveStatusesFetchPromise;
            setLeaveStatuses(mappedStatuses);
            // Only cache non-empty results so a bad/empty response can be retried
            if (mappedStatuses.length > 0) {
                cachedLeaveStatuses = mappedStatuses;
            } else {
                cachedLeaveStatuses = null;
            }
        } catch (error) {
            console.error('Error fetching leave statuses:', error);
            cachedLeaveStatuses = null;
        } finally {
            leaveStatusesFetchPromise = null;
        }
    };

    // Auto-select 'OFF' when Not Working is enabled and no reason is selected
    useEffect(() => {
        if (isNotWorking && !notWorkingReason && leaveStatuses.length > 0) {
            const offStatus = leaveStatuses.find(s => s.value === 'OFF');
            if (offStatus) {
                setNotWorkingReason('OFF');
                // Also clear error if it exists
                setErrors(prev => ({ ...prev, notWorkingReason: '' }));
            }
        }
    }, [isNotWorking, leaveStatuses]); // Removed notWorkingReason from dependencies to prevent loop if cleared by user

    useEffect(() => {
        if (isOpen) {
            // If startWithNotWorking is true, skip selection and go directly to single mode with not working enabled
            if (startWithNotWorking) {
                setMode('single');
                setEditType('single');
                setIsNotWorking(true);
            } else {
                setMode('selection');
                setEditType('single');
                setIsNotWorking(false);
            }
            setErrors({
                timingCode: '',
                checkIn: '',
                checkOut: '',
                notWorkingReason: ''
            });
            
            // Create day-to-date mapping from weekDates
            if (weekDates && weekDates.length > 0) {
                const dayMap = {};
                weekDates.forEach(date => {
                    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                    dayMap[dayName] = date;
                });
                setDayToDateMap(dayMap);
            }
            
            if (selectedDate) {
                const dayName = selectedDate.toLocaleDateString('en-US', { weekday: 'long' });
                setSelectedDay(dayName);
                setSelectedDayRepeating(`Every ${dayName}`); // Set the repeating day dropdown
                
                // Check if shiftInfo has leave = 1 (Not working/On leave)
                if (shiftInfo?.leave === 1) {
                    setIsNotWorking(true);
                    
                    // Pre-fill reason from status_name or by looking up status_id
                    if (shiftInfo.status_name) {
                        setNotWorkingReason(shiftInfo.status_name);
                    } else if (shiftInfo.status_id && cachedLeaveStatuses?.length) {
                        // Look up the status name from status_id
                        const statusOption = cachedLeaveStatuses.find(opt => opt.id === shiftInfo.status_id);
                        if (statusOption) {
                            setNotWorkingReason(statusOption.value);
                        } else {
                            setNotWorkingReason('');
                        }
                    } else {
                        setNotWorkingReason('');
                    }
                    
                    // Pre-fill remark if available
                    if (shiftInfo.remark) {
                        setRemarks(shiftInfo.remark);
                    } else {
                        setRemarks('');
                    }
                    // Reset timing code and times for leave
                    setTimingCode('');
                    setCheckIn('00:00');
                    setCheckOut('00:00');
                    setCheckOutExpansion(null);
                } else if (!startWithNotWorking) {
                    // Not on leave and not started with not working mode
                    setIsNotWorking(false);
                    setRemarks('');
                    setNotWorkingReason('');
                    
                    // Check if shiftInfo has timing_code_id and set as default
                    if (shiftInfo?.timing_code_id) {
                        setTimingCode(shiftInfo.timing_code_id.toString());
                        // Also set the times from shiftInfo if available
                        if (shiftInfo.checkin) {
                            setCheckIn(shiftInfo.checkin.substring(0, 5)); // Convert "10:00:00" to "10:00"
                        }
                        if (shiftInfo.checkout) {
                            setCheckOut(shiftInfo.checkout.substring(0, 5)); // Convert "20:00:00" to "20:00"
                        }
                        setCheckOutExpansion(getShiftExpansionFlag(shiftInfo));
                    } else if (shiftInfo?.checkin || shiftInfo?.checkout) {
                        // No timing code but has times - default to "Custom Schedule"
                        setTimingCode('custom');
                        // Use existing checkin/checkout times
                        if (shiftInfo.checkin) {
                            setCheckIn(shiftInfo.checkin.substring(0, 5)); // Convert "19:00:00" to "19:00"
                        } else {
                            setCheckIn('10:00');
                        }
                        if (shiftInfo.checkout) {
                            setCheckOut(shiftInfo.checkout.substring(0, 5)); // Convert "02:00:00" to "02:00"
                        } else {
                            setCheckOut('20:00');
                        }
                        setCheckOutExpansion(getShiftExpansionFlag(shiftInfo));
                    } else {
                        // No shift data at all - leave empty to show "Please select time code"
                        setTimingCode('');
                        setCheckIn('10:00');
                        setCheckOut('20:00');
                        setCheckOutExpansion(null);
                    }
                }
            }
        }
    }, [isOpen, selectedDate, shiftInfo, startWithNotWorking]);

    const getDayName = () => {
        if (!selectedDate) return '';
        return selectedDate.toLocaleDateString('en-US', { weekday: 'long' });
    };

    const getFormattedDate = () => {
        if (!selectedDate) return '';
        return selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getUpdateScope = (dayName) => {
        // Remove "Every " prefix if present (e.g., "Every Monday" -> "Monday")
        const cleanDayName = dayName.replace('Every ', '');
        
        const dayMap = {
            'Monday': 'all_mondays',
            'Tuesday': 'all_tuesdays',
            'Wednesday': 'all_wednesdays',
            'Thursday': 'all_thursdays',
            'Friday': 'all_fridays',
            'Saturday': 'all_saturdays',
            'Sunday': 'all_sundays'
        };
        return dayMap[cleanDayName] || 'all_mondays';
    };

    const formatDateToYMD = (date) => {
        // Convert to YYYY-MM-DD format
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const isToday = (date) => {
        if (!date) return false;
        const today = new Date();
        return date.getDate() === today.getDate() &&
               date.getMonth() === today.getMonth() &&
               date.getFullYear() === today.getFullYear();
    };

    const validateForm = () => {
        const newErrors = {
            timingCode: '',
            checkIn: '',
            checkOut: '',
            notWorkingReason: ''
        };
        let isValid = true;

        // If "not working" is selected, validate reason
        if (isNotWorking) {
            if (!notWorkingReason) {
                newErrors.notWorkingReason = 'Please select a reason';
                isValid = false;
            }
        } else {
            // If working, validate timing code and times
            if (!timingCode) {
                newErrors.timingCode = 'Please select a timing code';
                isValid = false;
            }
            
            // Both times cannot be 00:00, but either one can be (00:00 is valid for midnight)
            if (checkIn === '00:00' && checkOut === '00:00') {
                newErrors.checkIn = 'Please select start time';
                newErrors.checkOut = 'Please select end time';
                isValid = false;
            } else if (checkIn && checkOut && !isEndAfterStart(checkIn, checkOut, resolvedCheckOutExpansion === 1)) {
                newErrors.checkOut = 'Scheduled end time must be later than start time';
                isValid = false;
            }
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleNext = () => {
        if (editType === 'repeating') {
            setMode('repeating');
        } else {
            setMode('single');
        }
    };

    const handleCancel = () => {
        onClose();
    };

    const handleUpdateSchedule = async () => {
        if (loading) return;

        // Validate form first
        if (!validateForm()) {
            return;
        }

        if (!therapistId) {
            toast.error('Therapist ID is required');
            return;
        }

        if (!selectedDate) {
            toast.error('Date is required');
            return;
        }

        let response;
        let dataToPost;
        try {
            // Get the correct activity date based on selected day from the week dates mapping
            let activityDate = selectedDate;
            
            // In single mode, use the date from dayToDateMap for the selected day
            if (mode === 'single' && dayToDateMap[selectedDay]) {
                activityDate = dayToDateMap[selectedDay];
            }
            // In repeating mode, use the date from dayToDateMap for the selected repeating day
            else if (mode === 'repeating' && selectedDayRepeating) {
                const dayName = selectedDayRepeating.replace('Every ', ''); // "Every Monday" -> "Monday"
                if (dayToDateMap[dayName]) {
                    activityDate = dayToDateMap[dayName];
                }
            }
            
            // Build base payload
            dataToPost = {
                therapist_id: therapistId,
                activity_date: formatDateToYMD(activityDate),
                panel:"outlet",
            };

            // Determine update_scope based on mode
            if (mode === 'repeating') {
                dataToPost.update_scope = getUpdateScope(selectedDayRepeating);
            } else {
                // For single day, use 'this_day_only'
                dataToPost.update_scope = 'this_day_only';
            }

         

            if (leaveAnyway || shiftInfo?.has_current_date_booking) {
                dataToPost.leave_anyway = 1;
            }

            // If "Not working" is selected, add leave reason and set times to null
            if (isNotWorking) {
                dataToPost.leave = 1;
                dataToPost.reason = notWorkingReason;
                if (remarks) {
                    dataToPost.remark = remarks;
                }
                if(!shiftInfo?.has_current_date_booking){
                    dataToPost.hide = 1;
                }
                // Find the selected reason's id and pass as status_id
                const selectedReasonOption = leaveStatuses.find(opt => opt.value === notWorkingReason);
                if (selectedReasonOption && selectedReasonOption.id) {
                    dataToPost.status_id = selectedReasonOption.id;
                }
                // Set start_time and end_time to null for not working
                dataToPost.start_time = null;
                dataToPost.end_time = null;
            } else {
                // If working schedule, add times and timing code
                dataToPost.leave = 0;
                dataToPost.start_time = checkIn;
                dataToPost.end_time = checkOut;
                dataToPost.is_calendar_expansion = resolvedCheckOutExpansion === 1 ? 1 : 0;
                
                // Pass timing_code_id only if it's not 'custom'
                if (timingCode && timingCode !== 'custom') {
                    dataToPost.timing_code_id = timingCode;
                }
            }

            setLoading(true);
            response = await updateTherapistSchedule(dataToPost);

            if (response?.data?.success || response?.success) {
                toast.success(isNotWorking ? 'Leave updated successfully' : 'Schedule updated successfully');
                onClose();
                // Trigger refresh of work hours data
                if (onSuccess && typeof onSuccess === 'function') {
                    onSuccess();
                }
            } else {
                const errorMessage = response?.data?.message || response?.message || 'Failed to update schedule';
                toast.error(errorMessage);
                setLoading(false);
            }
        } catch (error) {
            console.error('Error updating schedule:', error);
            toast.error('An error occurred while updating schedule');
            setLoading(false);
        }finally{
            const isWorkHoursSource = from === 'workhour';
            const isSuccess = response?.data?.success || response?.success;
            // Only update Home list when leave/hide applies to Home's currently selected calendar date
            const activityDate = dataToPost?.activity_date;
            const homeDateYmd = homeSelectedDate instanceof Date && !isNaN(homeSelectedDate.getTime())
                ? formatDateToYMD(homeSelectedDate)
                : '';
            const matchesHomeSelectedDate = !!activityDate && activityDate === homeDateYmd;

            if (isWorkHoursSource && dataToPost?.hide === 1 && isSuccess && matchesHomeSelectedDate) {
                const newList = homeStaffList.filter(staff => String(staff.id) !== String(therapistId));
                setHomeStaffList(newList);
            }
        }
    };

    const renderSelectionMode = () => (
        <div className={styles.modeContent}>
            <h2 className={styles.modalTitle}>What would you like to edit?</h2>
            
            <div className={styles.optionsContainer}>
                <div 
                    className={`${styles.option} ${editType === 'repeating' ? styles.optionSelected : ''}`}
                    onClick={() => setEditType('repeating')}
                >
                    <div className={styles.optionContent}>
                        <div className={styles.optionTitle}>Repeating shifts</div>
                        <div className={styles.optionDescription}>
                            All {getDayName()}s beginning with {getFormattedDate()}
                        </div>
                    </div>
                    {editType === 'repeating' && (
                        <svg className={styles.checkIcon} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    )}
                </div>

                <div 
                    className={`${styles.option} ${editType === 'single' ? styles.optionSelected : ''}`}
                    onClick={() => setEditType('single')}
                >
                    <div className={styles.optionContent}>
                        <div className={styles.optionTitle}>This day only</div>
                        <div className={styles.optionDescription}>
                            Only {getFormattedDate()}
                        </div>
                    </div>
                    {editType === 'single' && (
                        <svg className={styles.checkIcon} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    )}
                </div>
            </div>

            <div className={styles.modalActions}>
                <button className={styles.cancelButton} onClick={handleCancel}>
                    Cancel
                </button>
                <button className={styles.nextButton} onClick={handleNext}>
                    Next
                </button>
            </div>
        </div>
    );

    const renderRepeatingMode = () => (
        <div className={styles.modeContent}>
            <h2 className={styles.modalTitle}>Select schedule for repeating shifts</h2>
            
            {!startWithNotWorking && (
                <label className={styles.toggleContainer}>
                    <input
                        type="checkbox"
                        checked={isNotWorking}
                        onChange={(e) => setIsNotWorking(e.target.checked)}
                        className={styles.toggleInput}
                    />
                    <span className={styles.toggleSlider}></span>
                    <span className={styles.toggleLabel}>Not working</span>
                </label>
            )}

            {!isNotWorking && (
                <div className={styles.scheduleForm}>
                    <div className={styles.scheduleGrid}>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Day</label>
                            <DropdownNoImage
                                options={daysRepeating.map(day => ({ label: day, value: day }))}
                                defaultOption={selectedDayRepeating}
                                onSelect={(option) => setSelectedDayRepeating(option.value)}
                                dropdownClass={styles.customDropdown}
                                dropdownMenuClass={styles.customDropdownMenu}
                                labelText=""
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Timing Code</label>
                            <DropdownNoImage
                                options={timingCodes}
                                defaultOption={timingCode ? timingCodes.find(tc => tc.value === timingCode)?.label || 'Please select time code' : 'Please select time code'}
                                onSelect={(option) => {
                                    setTimingCode(option.value);
                                    // Clear timing code error when selected
                                    setErrors(prev => ({ ...prev, timingCode: '' }));
                                    // Auto-fill times based on timing code
                                    if (option.value === 'custom') {
                                        // For custom schedule, keep default working hours or current values
                                        // Don't reset if user already has times set
                                        if (checkIn === '00:00' && checkOut === '00:00') {
                                            setCheckIn('10:00');
                                            setCheckOut('20:00');
                                        }
                                        setCheckOutExpansion(null);
                                        // User can manually adjust times for custom schedule
                                    } else if (option.start_time && option.end_time) {
                                        // For other timing codes, use their times
                                        const startTime = option.start_time.substring(0, 5);
                                        const endTime = option.end_time.substring(0, 5);
                                        setCheckIn(startTime);
                                        setCheckOut(endTime);
                                        setCheckOutExpansion(doesTimeRangeCrossMidnight(startTime, endTime) ? 1 : 0);
                                        // Clear time errors when times are auto-filled
                                        setErrors(prev => ({ ...prev, checkIn: '', checkOut: '' }));
                                    }
                                }}
                                dropdownClass={styles.customDropdown}
                                dropdownMenuClass={styles.customDropdownMenu}
                                labelText=""
                            />
                            {errors.timingCode && (
                                <span className={styles.customScheduleError}>{errors.timingCode}</span>
                            )}
                            {errors.checkOut && timingCode !== 'custom' && (
                                <span className={styles.customScheduleError}>{errors.checkOut}</span>
                            )}
                        </div>

                        {timingCode === 'custom' && (
                            <>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Scheduled Start Time</label>
                                    <div className={styles.timeInputGroup}>
                                        <div className={styles.timeInputWrapper}>
                                            <input
                                                type="time"
                                                value={checkIn}
                                                onChange={(e) => handleCheckInInputChange(e.target.value)}
                                                onKeyDown={(e) => handleTimeInputKeyDown(e, 'checkIn')}
                                                onBlur={() => clearPendingHourDigit('checkIn')}
                                                onClick={(e) => handleTimeInputClick(e, 'checkIn', checkIn)}
                                                onDoubleClick={(e) => handleTimeInputDoubleClick(e, 'checkIn')}
                                                className={styles.timeInput}
                                                step="60"
                                            />
                                        </div>
                                        <div className={styles.timeDropdownWrapper}>
                                            <DropdownNoLabel
                                                options={startTimeOptions}
                                                selectedLabel=""
                                                selectedValue={checkIn}
                                                onSelect={(option) => handleTimeDropdownSelect('checkIn', option)}
                                                dropdownClass={`${styles.timeDropdown} ${styles.hiddenDropdownToggle}`}
                                                placeholder=""
                                                placeholderClass={styles.placeholder}
                                            />
                                        </div>
                                    </div>
                                    {errors.checkIn && (
                                        <span className={styles.customScheduleError}>{errors.checkIn}</span>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Scheduled End Time</label>
                                    <div className={styles.timeInputGroup}>
                                        <div className={styles.timeInputWrapper}>
                                            <input
                                                type="time"
                                                value={checkOut}
                                                onChange={(e) => handleCheckOutInputChange(e.target.value)}
                                                onKeyDown={(e) => handleTimeInputKeyDown(e, 'checkOut')}
                                                onBlur={() => clearPendingHourDigit('checkOut')}
                                                onClick={(e) => handleTimeInputClick(e, 'checkOut', checkOut)}
                                                onDoubleClick={(e) => handleTimeInputDoubleClick(e, 'checkOut')}
                                                className={styles.timeInput}
                                                step="60"
                                            />
                                            {shouldShowExpandedEndLabel && (
                                                <span className={styles.timeExpansionSuffix}>+1</span>
                                            )}
                                        </div>
                                        <div className={styles.timeDropdownWrapper}>
                                            <DropdownNoLabel
                                                options={endTimeOptions}
                                                selectedLabel=""
                                                selectedValue={selectedEndTimeValue}
                                                onSelect={(option) => handleTimeDropdownSelect('checkOut', option)}
                                                dropdownClass={`${styles.timeDropdown} ${styles.hiddenDropdownToggle}`}
                                                placeholder=""
                                                placeholderClass={styles.placeholder}
                                            />
                                        </div>
                                    </div>
                                    {errors.checkOut && (
                                        <span className={styles.customScheduleError}>{errors.checkOut}</span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {isNotWorking && (
                <div className={styles.notWorkingForm}>
                    {!startWithNotWorking && shiftInfo?.has_current_date_booking && (() => {
                        // Check if the selected repeating day is today
                        const dayName = selectedDayRepeating.replace('Every ', '');
                        const selectedDateForDay = dayToDateMap[dayName];
                        return isToday(selectedDateForDay);
                    })() && (
                        <div className={styles.activeBookingWarning}>
                            This therapist has active bookings.
                            <div className={styles.bookingTip}>
                                <strong>Tip:</strong> Therapists will be shown on the calendar even on off days as long as they have an active booking.
                            </div>
                        </div>
                    )}
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Select reason</label>
                        <DropdownNoImage
                            options={leaveStatuses}
                            defaultOption={notWorkingReason || 'Select reason'}
                            onSelect={(option) => {
                                setNotWorkingReason(option.value);
                                // Clear error when user selects a reason
                                setErrors(prev => ({ ...prev, notWorkingReason: '' }));
                            }}
                            dropdownClass={styles.customDropdown}
                            dropdownMenuClass={styles.customDropdownMenu}
                            labelText=""
                        />
                        {errors.notWorkingReason && (
                            <span className={styles.customScheduleError}>{errors.notWorkingReason}</span>
                        )}
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Reason/Remark (optional)</label>
                        <input
                            type="text"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            className={styles.remarkInput}
                            placeholder="Enter remarks..."
                        />
                    </div>
                </div>
            )}

            <div className={styles.modalActions}>
                <button className={styles.cancelButton} onClick={handleCancel}>
                    Cancel
                </button>
                <button 
                    className={styles.updateButton} 
                    onClick={handleUpdateSchedule}
                >
                    Update Schedule
                </button>
            </div>
        </div>
    );

    const renderSingleMode = () => (
        <div className={styles.modeContent}>
            <h2 className={styles.modalTitle}>{startWithNotWorking ? 'Set to Not Working' : 'Select schedule for today only'}</h2>
            
            {!startWithNotWorking && (
                <label className={styles.toggleContainer}>
                    <input
                        type="checkbox"
                        checked={isNotWorking}
                        onChange={(e) => setIsNotWorking(e.target.checked)}
                        className={styles.toggleInput}
                    />
                    <span className={styles.toggleSlider}></span>
                    <span className={styles.toggleLabel}>Not working</span>
                </label>
            )}

            {isNotWorking && (
                <div className={styles.notWorkingForm}>
                    {shiftInfo?.has_current_date_booking && (
                        <div className={styles.activeBookingWarning}>
                            This therapist has active bookings.
                            <div className={styles.bookingTip}>
                                <strong>Tip:</strong> Therapists will be shown on the calendar even on off days as long as they have an active booking.
                            </div>
                        </div>
                    )}
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Select reason</label>
                        <DropdownNoImage
                            options={leaveStatuses}
                            defaultOption={notWorkingReason || 'Select reason'}
                            onSelect={(option) => {
                                setNotWorkingReason(option.value);
                                // Clear error when user selects a reason
                                setErrors(prev => ({ ...prev, notWorkingReason: '' }));
                            }}
                            dropdownClass={styles.customDropdown}
                            dropdownMenuClass={styles.customDropdownMenu}
                            labelText=""
                        />
                        {errors.notWorkingReason && (
                            <span className={styles.customScheduleError}>{errors.notWorkingReason}</span>
                        )}
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Reason/Remark (optional)</label>
                        <input
                            type="text"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            className={styles.remarkInput}
                            placeholder="Enter remarks..."
                        />
                    </div>
                </div>
            )}

            {!isNotWorking && (
                <div className={styles.scheduleForm}>
                    <div className={styles.scheduleGrid}>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Day</label>
                            <DropdownNoImage
                                options={days.map(day => ({ label: day, value: day }))}
                                defaultOption={selectedDay}
                                onSelect={(option) => setSelectedDay(option.value)}
                                dropdownClass={styles.customDropdown}
                                dropdownMenuClass={styles.customDropdownMenu}
                                labelText=""
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Timing Code</label>
                            <DropdownNoImage
                                options={timingCodes}
                                defaultOption={timingCode ? timingCodes.find(tc => tc.value === timingCode)?.label || 'Please select time code' : 'Please select time code'}
                                onSelect={(option) => {
                                    setTimingCode(option.value);
                                    // Clear timing code error when selected
                                    setErrors(prev => ({ ...prev, timingCode: '' }));
                                    // Auto-fill times based on timing code
                                    if (option.value === 'custom') {
                                        // For custom schedule, keep default working hours or current values
                                        // Don't reset if user already has times set
                                        if (checkIn === '00:00' && checkOut === '00:00') {
                                            setCheckIn('10:00');
                                            setCheckOut('20:00');
                                        }
                                        setCheckOutExpansion(null);
                                        // User can manually adjust times for custom schedule
                                    } else if (option.start_time && option.end_time) {
                                        // For other timing codes, use their times
                                        const startTime = option.start_time.substring(0, 5);
                                        const endTime = option.end_time.substring(0, 5);
                                        setCheckIn(startTime);
                                        setCheckOut(endTime);
                                        setCheckOutExpansion(doesTimeRangeCrossMidnight(startTime, endTime) ? 1 : 0);
                                        // Clear time errors when times are auto-filled
                                        setErrors(prev => ({ ...prev, checkIn: '', checkOut: '' }));
                                    }
                                }}
                                dropdownClass={styles.customDropdown}
                                dropdownMenuClass={styles.customDropdownMenu}
                                labelText=""
                            />
                            {errors.timingCode && (
                                <span className={styles.customScheduleError}>{errors.timingCode}</span>
                            )}
                            {errors.checkOut && timingCode !== 'custom' && (
                                <span className={styles.customScheduleError}>{errors.checkOut}</span>
                            )}
                        </div>

                        {timingCode === 'custom' && (
                            <>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Scheduled Start Time</label>
                                    <div className={styles.timeInputGroup}>
                                        <div className={styles.timeInputWrapper}>
                                            <input
                                                type="time"
                                                value={checkIn}
                                                onChange={(e) => handleCheckInInputChange(e.target.value)}
                                                onKeyDown={(e) => handleTimeInputKeyDown(e, 'checkIn')}
                                                onBlur={() => clearPendingHourDigit('checkIn')}
                                                onClick={(e) => handleTimeInputClick(e, 'checkIn', checkIn)}
                                                onDoubleClick={(e) => handleTimeInputDoubleClick(e, 'checkIn')}
                                                className={styles.timeInput}
                                                step="60"
                                            />
                                        </div>
                                        <div className={styles.timeDropdownWrapper}>
                                            <DropdownNoLabel
                                                options={startTimeOptions}
                                                selectedLabel=""
                                                selectedValue={checkIn}
                                                onSelect={(option) => handleTimeDropdownSelect('checkIn', option)}
                                                dropdownClass={`${styles.timeDropdown} ${styles.hiddenDropdownToggle}`}
                                                placeholder=""
                                                placeholderClass={styles.placeholder}
                                            />
                                        </div>
                                    </div>
                                    {errors.checkIn && (
                                        <span className={styles.customScheduleError}>{errors.checkIn}</span>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Scheduled End Time</label>
                                    <div className={styles.timeInputGroup}>
                                        <div className={styles.timeInputWrapper}>
                                            <input
                                                type="time"
                                                value={checkOut}
                                                onChange={(e) => handleCheckOutInputChange(e.target.value)}
                                                onKeyDown={(e) => handleTimeInputKeyDown(e, 'checkOut')}
                                                onBlur={() => clearPendingHourDigit('checkOut')}
                                                onClick={(e) => handleTimeInputClick(e, 'checkOut', checkOut)}
                                                onDoubleClick={(e) => handleTimeInputDoubleClick(e, 'checkOut')}
                                                className={styles.timeInput}
                                                step="60"
                                            />
                                            {shouldShowExpandedEndLabel && (
                                                <span className={styles.timeExpansionSuffix}>+1</span>
                                            )}
                                        </div>
                                        <div className={styles.timeDropdownWrapper}>
                                            <DropdownNoLabel
                                                options={endTimeOptions}
                                                selectedLabel=""
                                                selectedValue={selectedEndTimeValue}
                                                onSelect={(option) => handleTimeDropdownSelect('checkOut', option)}
                                                dropdownClass={`${styles.timeDropdown} ${styles.hiddenDropdownToggle}`}
                                                placeholder=""
                                                placeholderClass={styles.placeholder}
                                            />
                                        </div>
                                    </div>
                                    {errors.checkOut && (
                                        <span className={styles.customScheduleError}>{errors.checkOut}</span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className={styles.modalActions}>
                <button className={styles.cancelButton} onClick={handleCancel}>
                    Cancel
                </button>
                <button 
                    className={styles.updateButton} 
                    onClick={handleUpdateSchedule}
                >
                    Update Schedule
                </button>
            </div>
        </div>
    );

    return (
        <CustomModal 
            show={isOpen} 
            onHide={onClose}
            hideCloseButton={true}
        >
            <div className={styles.modalContent}>
                <div className={`${styles.modeWrapper} ${styles[`mode-${mode}`]}`}>
                    {mode === 'selection' && renderSelectionMode()}
                    {mode === 'repeating' && renderRepeatingMode()}
                    {mode === 'single' && renderSingleMode()}
                </div>
            </div>
        </CustomModal>
    );
};

export default WorkHoursPopup;